/* @img-migrated */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Save, X, Upload, Eye, EyeOff,
  ChevronUp, ChevronDown, User, Link2, Search, Crown, Users, Award, Briefcase,
  ExternalLink, Globe, UserRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'founder', label: 'Our Founder', icon: Crown, color: '#E63946' },
  { value: 'executive', label: 'Executive Board', icon: Award, color: '#002249' },
  { value: 'director', label: 'Board of Directors', icon: Users, color: '#457B9D' },
  { value: 'committee', label: 'Committee Directors', icon: Briefcase, color: '#1D3557' },
];

const emptyLeader = {
  name: '', title: '', category: 'director', bio: '', full_bio: '',
  email: '', phone: '', linkedin: '', order: 99, is_active: true, user_id: null,
};

const LeadershipManagementPage = () => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLeader, setEditingLeader] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const fileRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => { fetchLeaders(); }, []);

  const fetchLeaders = async () => {
    try {
      const res = await axios.get(`${API}/admin/leaders`, { withCredentials: true });
      setLeaders(res.data);
    } catch (err) {
      toast.error('Failed to load leaders');
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/leaders/users-list`, { withCredentials: true });
      setUsersList(res.data);
    } catch { /* */ }
  };

  const handleEdit = (leader) => {
    setEditingLeader({ ...leader });
    fetchUsers();
  };

  const handleNew = () => {
    const maxOrder = leaders.length > 0 ? Math.max(...leaders.map(l => l.order || 0)) + 1 : 1;
    setEditingLeader({ ...emptyLeader, order: maxOrder });
    fetchUsers();
  };

  const handleSave = async () => {
    if (!editingLeader.name.trim()) { toast.error('Name is required'); return; }
    if (!editingLeader.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editingLeader.id) {
        await axios.put(`${API}/admin/leaders/${editingLeader.id}`, editingLeader, { withCredentials: true });
        toast.success('Leader updated');
      } else {
        await axios.post(`${API}/admin/leaders`, editingLeader, { withCredentials: true });
        toast.success('Leader created');
      }
      setEditingLeader(null);
      fetchLeaders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leaders/${id}`, { withCredentials: true });
      toast.success('Leader deleted');
      fetchLeaders();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleToggleActive = async (leader) => {
    try {
      await axios.put(`${API}/admin/leaders/${leader.id}`, { is_active: !leader.is_active }, { withCredentials: true });
      toast.success(leader.is_active ? 'Leader hidden from public' : 'Leader visible publicly');
      fetchLeaders();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingLeader?.id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/leaders/${editingLeader.id}/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditingLeader(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Photo uploaded');
      fetchLeaders();
    } catch (err) {
      toast.error('Photo upload failed');
    } finally { setUploading(false); }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingLeader?.id) return;
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/leaders/${editingLeader.id}/banner`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditingLeader(prev => ({ ...prev, banner_url: res.data.banner_url }));
      toast.success('Banner uploaded');
      fetchLeaders();
    } catch (err) {
      toast.error('Banner upload failed');
    } finally { setUploadingBanner(false); }
  };

  const handleBannerRemove = async () => {
    if (!editingLeader?.id) return;
    if (!window.confirm('Remove custom banner? Default will be used instead.')) return;
    try {
      await axios.delete(`${API}/admin/leaders/${editingLeader.id}/banner`, { withCredentials: true });
      setEditingLeader(prev => ({ ...prev, banner_url: null }));
      toast.success('Banner removed');
      fetchLeaders();
    } catch (err) {
      toast.error('Failed to remove banner');
    }
  };

  const handleMoveOrder = async (leader, direction) => {
    const catLeaders = leaders
      .filter(l => l.category === leader.category)
      .sort((a, b) => a.order - b.order);
    const idx = catLeaders.findIndex(l => l.id === leader.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= catLeaders.length) return;
    const orders = catLeaders.map((l, i) => {
      if (i === idx) return { id: l.id, order: catLeaders[swapIdx].order };
      if (i === swapIdx) return { id: l.id, order: catLeaders[idx].order };
      return { id: l.id, order: l.order };
    });
    try {
      await axios.put(`${API}/admin/leaders/reorder`, { orders }, { withCredentials: true });
      fetchLeaders();
    } catch (err) { toast.error('Reorder failed'); }
  };

  const filteredUsers = usersList.filter(u =>
    (u.name || u.display_name || u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const getLinkedUserName = (userId) => {
    const u = usersList.find(u => u.id === userId);
    return u ? (u.display_name || u.name || u.email) : userId;
  };

  const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[2];
  const inputClass = "w-full border border-slate-200 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

  // ===== EDIT FORM =====
  if (editingLeader) {
    return (
      <div className="p-6 lg:p-8" data-testid="leader-editor">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#002249]">{editingLeader.id ? 'Edit Leader' : 'Add New Leader'}</h1>
          <Button variant="outline" onClick={() => setEditingLeader(null)} className="rounded-sm" data-testid="cancel-leader-edit">
            <X className="w-4 h-4 mr-1" />Cancel
          </Button>
        </div>
        <div className="max-w-3xl space-y-6">
          {/* Photo */}
          {editingLeader.id && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <h2 className="font-bold text-[#002249] mb-4">Photo</h2>
              <div className="flex items-center gap-6">
                <div className="w-28 h-28 rounded-sm bg-gradient-to-br from-[#002249] to-[#457B9D] overflow-hidden flex-shrink-0">
                  {editingLeader.image_url ? (
                    <Img variant="leader" src={resolveImg(editingLeader.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><User className="w-12 h-12 text-white/30" /></div>
                  )}
                </div>
                <div>
                  <input type="file" ref={fileRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="upload-leader-photo">
                    <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">JPG, PNG. Recommended 400x400px.</p>
                </div>
              </div>
            </div>
          )}

          {/* Official Banner */}
          {editingLeader.id && (
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="banner-section">
              <h2 className="font-bold text-[#002249] mb-1">Community Profile Banner</h2>
              <p className="text-xs text-slate-400 mb-4">This banner appears on the leader's portal/community profile. If none is set, the default DCCF leadership banner is used.</p>
              <div className="space-y-3">
                {/* Banner preview */}
                <div className="w-full h-32 sm:h-40 rounded-sm overflow-hidden bg-gradient-to-r from-[#002249] via-[#0a3570] to-[#1D3557] relative">
                  {editingLeader.banner_url ? (
                    <Img variant="leader" src={resolveImg(editingLeader.banner_url)}
                      alt="Banner preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white/30 text-xs font-medium tracking-wide">Default DCCF Leadership Banner</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input type="file" ref={bannerRef} onChange={handleBannerUpload} accept="image/*" className="hidden" />
                  <Button variant="outline" onClick={() => bannerRef.current?.click()} disabled={uploadingBanner} className="rounded-sm" data-testid="upload-leader-banner">
                    <Upload className="w-4 h-4 mr-2" />{uploadingBanner ? 'Uploading...' : (editingLeader.banner_url ? 'Replace Banner' : 'Upload Banner')}
                  </Button>
                  {editingLeader.banner_url && (
                    <Button variant="outline" onClick={handleBannerRemove} className="rounded-sm text-red-500 hover:text-red-700 border-red-200 hover:border-red-300" data-testid="remove-leader-banner">
                      <Trash2 className="w-4 h-4 mr-2" />Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400">Recommended: 1920x400px (5:1 panoramic). JPG/PNG.</p>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Full Name *</label>
                <input value={editingLeader.name} onChange={e => setEditingLeader({...editingLeader, name: e.target.value})} className={inputClass} data-testid="leader-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Title / Position *</label>
                <input value={editingLeader.title} onChange={e => setEditingLeader({...editingLeader, title: e.target.value})} className={inputClass} data-testid="leader-title" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                <select value={editingLeader.category} onChange={e => setEditingLeader({...editingLeader, category: e.target.value})} className={inputClass} data-testid="leader-category">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Order</label>
                <input type="number" min="0" value={editingLeader.order} onChange={e => setEditingLeader({...editingLeader, order: parseInt(e.target.value) || 0})} className={inputClass} data-testid="leader-order" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Short Bio (shown on card)</label>
                <textarea value={editingLeader.bio || ''} onChange={e => setEditingLeader({...editingLeader, bio: e.target.value})} className={`${inputClass} min-h-[80px]`} data-testid="leader-bio" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Full Biography (shown on profile page)</label>
                <textarea value={editingLeader.full_bio || ''} onChange={e => setEditingLeader({...editingLeader, full_bio: e.target.value})} className={`${inputClass} min-h-[120px]`} data-testid="leader-full-bio" />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                <input type="email" value={editingLeader.email || ''} onChange={e => setEditingLeader({...editingLeader, email: e.target.value})} className={inputClass} data-testid="leader-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                <input value={editingLeader.phone || ''} onChange={e => setEditingLeader({...editingLeader, phone: e.target.value})} className={inputClass} data-testid="leader-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">LinkedIn URL</label>
                <input value={editingLeader.linkedin || ''} onChange={e => setEditingLeader({...editingLeader, linkedin: e.target.value})} placeholder="https://linkedin.com/in/..." className={inputClass} data-testid="leader-linkedin" />
              </div>
            </div>
          </div>

          {/* Visibility & Linking */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="font-bold text-[#002249] mb-4">Visibility & User Linking</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="leader-active-toggle">
                <div className={`w-10 h-6 rounded-full transition-colors ${editingLeader.is_active ? 'bg-[#002249]' : 'bg-slate-300'} relative`}
                  onClick={() => setEditingLeader({...editingLeader, is_active: !editingLeader.is_active})}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${editingLeader.is_active ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {editingLeader.is_active ? 'Visible on public page' : 'Hidden from public page'}
                </span>
              </label>

              <div className="border-t border-slate-100 pt-4">
                <label className="block text-xs font-medium text-slate-600 mb-2">Link to Portal User Account (optional)</label>
                <p className="text-xs text-slate-400 mb-3">Connect this leader to an existing user account for portal access.</p>
                {editingLeader.user_id ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <Link2 className="w-4 h-4 text-[#002249]" />
                    <span className="text-sm font-medium text-[#002249]">{getLinkedUserName(editingLeader.user_id)}</span>
                    <button onClick={() => setEditingLeader({...editingLeader, user_id: null})} className="ml-auto text-xs text-red-500 hover:text-red-700">Unlink</button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex items-center border border-slate-200 rounded-sm">
                      <Search className="w-4 h-4 ml-3 text-slate-400" />
                      <input
                        value={userSearch}
                        onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                        onFocus={() => setShowUserDropdown(true)}
                        placeholder="Search users by name or email..."
                        className="w-full p-2.5 text-sm focus:outline-none"
                        data-testid="leader-user-search"
                      />
                    </div>
                    <AnimatePresence>
                      {showUserDropdown && userSearch.length > 0 && (
                        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                          {filteredUsers.length === 0 ? (
                            <div className="p-3 text-xs text-slate-400">No matching users</div>
                          ) : filteredUsers.slice(0, 10).map(u => (
                            <button key={u.id} onClick={() => { setEditingLeader({...editingLeader, user_id: u.id}); setShowUserDropdown(false); setUserSearch(''); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b border-slate-50 last:border-0"
                              data-testid={`link-user-${u.id}`}>
                              <div className="w-6 h-6 bg-[#002249]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-3 h-3 text-[#002249]" />
                              </div>
                              <div>
                                <span className="font-medium text-slate-800">{u.display_name || u.name || 'Unnamed'}</span>
                                <span className="text-slate-400 ml-2">{u.email}</span>
                              </div>
                              <span className="ml-auto text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500">{u.role}</span>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#002249]/90 text-white rounded-sm px-8" data-testid="save-leader-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Leader'}
            </Button>
            <Button variant="outline" onClick={() => setEditingLeader(null)} className="rounded-sm">Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== LIST VIEW =====
  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    leaders: leaders.filter(l => l.category === cat.value).sort((a, b) => a.order - b.order),
  }));

  return (
    <div className="p-6 lg:p-8" data-testid="leadership-management">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Leadership Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the public "Meet Our Leaders" page</p>
        </div>
        <Button onClick={handleNew} className="bg-[#E63946] hover:bg-[#E63946]/90 text-white rounded-sm" data-testid="add-leader-btn">
          <Plus className="w-4 h-4 mr-2" />Add Leader
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#002249] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(group => {
            const Icon = group.icon;
            return (
              <div key={group.value} data-testid={`category-${group.value}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${group.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: group.color }} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: group.color }}>{group.label}</h2>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{group.leaders.length}</span>
                </div>

                {group.leaders.length === 0 ? (
                  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-sm p-6 text-center">
                    <p className="text-sm text-slate-400">No leaders in this category</p>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                    {group.leaders.map((leader, idx) => (
                      <div key={leader.id} className={`flex items-center gap-4 p-4 ${idx > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}
                        data-testid={`leader-row-${leader.id}`}>
                        {/* Photo */}
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#002249] to-[#457B9D] overflow-hidden flex-shrink-0">
                          {leader.image_url ? (
                            <Img variant="leader" src={resolveImg(leader.image_url)}
                              alt={leader.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-white/40" /></div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-800 text-sm truncate">{leader.name}</h3>
                            {!leader.is_active && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">HIDDEN</span>
                            )}
                            {leader.user_id && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                                <Link2 className="w-2.5 h-2.5" />LINKED
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{leader.title}</p>
                        </div>

                        {/* Order */}
                        <div className="hidden sm:flex items-center gap-1">
                          <span className="text-xs text-slate-400 mr-1">#{leader.order}</span>
                          <button onClick={() => handleMoveOrder(leader, 'up')} className="p-1 hover:bg-slate-100 rounded" data-testid={`move-up-${leader.id}`}>
                            <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                          <button onClick={() => handleMoveOrder(leader, 'down')} className="p-1 hover:bg-slate-100 rounded" data-testid={`move-down-${leader.id}`}>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <a
                            href={`/leadership/${leader.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 hover:bg-amber-50 rounded-sm transition-colors"
                            title="View Public Leadership Profile (opens in new tab)"
                            data-testid={`view-public-profile-${leader.id}`}
                          >
                            <Globe className="w-4 h-4 text-amber-600" />
                          </a>
                          {leader.user_id && (
                            <a
                              href={`/portal/members/${leader.user_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-2 hover:bg-[#002249]/10 rounded-sm transition-colors"
                              title="View Community Profile (opens in new tab)"
                              data-testid={`view-community-profile-${leader.id}`}
                            >
                              <UserRound className="w-4 h-4 text-[#002249]" />
                            </a>
                          )}
                          <button onClick={() => handleToggleActive(leader)} className="p-2 hover:bg-slate-100 rounded-sm transition-colors" title={leader.is_active ? 'Hide' : 'Show'}
                            data-testid={`toggle-active-${leader.id}`}>
                            {leader.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                          </button>
                          <button onClick={() => handleEdit(leader)} className="p-2 hover:bg-slate-100 rounded-sm transition-colors" data-testid={`edit-leader-${leader.id}`}>
                            <Edit2 className="w-4 h-4 text-[#002249]" />
                          </button>
                          <button onClick={() => handleDelete(leader.id, leader.name)} className="p-2 hover:bg-red-50 rounded-sm transition-colors" data-testid={`delete-leader-${leader.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadershipManagementPage;
